import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2, Mail, Phone, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const SIGNATURE_URL =
    "https://customer-assets.emergentagent.com/job_afad2a6d-7dba-488f-b2fd-a41c3f20dfe4/artifacts/j9q23nay_BPO%20Solutions%202026%20Handtekening%20signature.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
    "Strategy & Performance Diagnostics",
    "Operational Excellence (Six Sigma)",
    "Change & Adoption (Prosci)",
    "Programme Delivery (PMP)",
    "Vested® Sourcing",
    "Customer Experience (COPC)",
    "Not sure yet — exploratory call",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        interest: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const update = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and a short message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSuccess(true);
            toast.success("Thanks — we’ll be in touch within one business day.");
            setForm({ name: "", email: "", company: "", interest: "", message: "" });
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Something went wrong. Please email bposolutions.info@gmail.com directly.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-20 md:py-28 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 md:px-10">
                <div
                    data-testid="contact-card"
                    className="relative overflow-hidden rounded-[28px] bg-bpo-ink text-white shadow-2xl"
                >
                    <div
                        aria-hidden
                        className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(closest-side, rgba(45,212,212,0.35), transparent 70%)",
                        }}
                    />
                    <div className="relative grid lg:grid-cols-12 gap-10 p-8 md:p-14">
                        {/* Left: copy + contact info */}
                        <div className="lg:col-span-5">
                            <span className="eyebrow text-bpo-cyan">
                                Start a conversation
                            </span>
                            <h2 className="font-display mt-3 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
                                Tell us where you’re going.
                                <span className="block text-bpo-cyan">
                                    We’ll map the next step.
                                </span>
                            </h2>
                            <p className="mt-5 text-white/75 text-base md:text-lg leading-relaxed">
                                Book a 30-minute discovery call or send a brief.
                                We typically reply within one business day.
                            </p>

                            <a
                                href="https://app.kadonetworks.com/kado/bposolutions"
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid="contact-kado-link"
                                className="mt-7 inline-flex items-center gap-2 bg-bpo-cyan hover:bg-bpo-cyanDark text-bpo-ink font-semibold py-3 px-6 rounded-full transition-all hover:-translate-y-0.5"
                            >
                                Open digital business card
                                <ArrowUpRight className="h-4 w-4" />
                            </a>

                            <ul className="mt-10 space-y-4 text-sm">
                                <li className="flex items-start gap-3">
                                    <Mail className="h-4 w-4 mt-1 text-bpo-cyan shrink-0" />
                                    <a
                                        href="mailto:bposolutions.info@gmail.com"
                                        className="text-white hover:text-bpo-cyan transition-colors break-all"
                                        data-testid="contact-email-link"
                                    >
                                        bposolutions.info@gmail.com
                                    </a>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Phone className="h-4 w-4 mt-1 text-bpo-cyan shrink-0" />
                                    <a
                                        href="tel:+31615532712"
                                        className="text-white hover:text-bpo-cyan transition-colors"
                                        data-testid="contact-phone-link"
                                    >
                                        +31 6155 327 12
                                    </a>
                                </li>
                                <li className="flex items-start gap-3">
                                    <MapPin className="h-4 w-4 mt-1 text-bpo-cyan shrink-0" />
                                    <span className="text-white/80 leading-relaxed">
                                        William Gladstone 30, fl. 2 · Triaditsa
                                        District 1000 · Sofia, Bulgaria
                                        <span className="block text-white/50 text-xs mt-1">
                                            (Entity registration — not for visits)
                                        </span>
                                    </span>
                                </li>
                            </ul>

                            <div className="mt-10 hidden md:block">
                                <img
                                    src={SIGNATURE_URL}
                                    alt="BPO Solutions signature"
                                    className="max-w-[360px] w-full opacity-90 bg-white rounded-2xl p-3"
                                />
                            </div>
                        </div>

                        {/* Right: form */}
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="lg:col-span-7 rounded-2xl bg-white text-bpo-ink p-6 md:p-8 shadow-xl"
                        >
                            {success && (
                                <div
                                    data-testid="contact-success-banner"
                                    className="mb-6 flex items-start gap-3 rounded-xl border border-bpo-cyan/30 bg-bpo-cyanLight p-4"
                                >
                                    <CheckCircle2 className="h-5 w-5 text-bpo-cyanDark mt-0.5" />
                                    <div>
                                        <div className="font-semibold text-bpo-ink">
                                            Brief received.
                                        </div>
                                        <div className="text-sm text-bpo-charcoal/80">
                                            We’ll reply within one business day.
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <Label
                                        htmlFor="contact-name"
                                        className="text-xs uppercase tracking-[0.15em] font-semibold text-bpo-charcoal"
                                    >
                                        Full name
                                    </Label>
                                    <Input
                                        id="contact-name"
                                        data-testid="contact-input-name"
                                        type="text"
                                        autoComplete="name"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Jane Doe"
                                        className="mt-2 h-12 bg-gray-50 border-gray-200 focus-visible:ring-bpo-cyan/40 focus-visible:border-bpo-cyan"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="contact-email"
                                        className="text-xs uppercase tracking-[0.15em] font-semibold text-bpo-charcoal"
                                    >
                                        Work email
                                    </Label>
                                    <Input
                                        id="contact-email"
                                        data-testid="contact-input-email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="jane@company.com"
                                        className="mt-2 h-12 bg-gray-50 border-gray-200 focus-visible:ring-bpo-cyan/40 focus-visible:border-bpo-cyan"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="contact-company"
                                        className="text-xs uppercase tracking-[0.15em] font-semibold text-bpo-charcoal"
                                    >
                                        Company
                                    </Label>
                                    <Input
                                        id="contact-company"
                                        data-testid="contact-input-company"
                                        type="text"
                                        value={form.company}
                                        onChange={update("company")}
                                        placeholder="Company name"
                                        className="mt-2 h-12 bg-gray-50 border-gray-200 focus-visible:ring-bpo-cyan/40 focus-visible:border-bpo-cyan"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-bpo-charcoal">
                                        Area of interest
                                    </Label>
                                    <Select
                                        value={form.interest}
                                        onValueChange={(v) =>
                                            setForm((s) => ({ ...s, interest: v }))
                                        }
                                    >
                                        <SelectTrigger
                                            data-testid="contact-select-interest"
                                            className="mt-2 h-12 bg-gray-50 border-gray-200 focus:ring-bpo-cyan/40"
                                        >
                                            <SelectValue placeholder="Choose a topic" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {INTERESTS.map((i) => (
                                                <SelectItem
                                                    key={i}
                                                    value={i}
                                                    data-testid={`interest-option-${i
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, "-")}`}
                                                >
                                                    {i}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="mt-5">
                                <Label
                                    htmlFor="contact-message"
                                    className="text-xs uppercase tracking-[0.15em] font-semibold text-bpo-charcoal"
                                >
                                    What are you working on?
                                </Label>
                                <Textarea
                                    id="contact-message"
                                    data-testid="contact-input-message"
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder="Briefly describe your context, ambition and any timeline…"
                                    className="mt-2 bg-gray-50 border-gray-200 focus-visible:ring-bpo-cyan/40 focus-visible:border-bpo-cyan resize-none"
                                />
                            </div>

                            <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="text-xs text-bpo-charcoal/60 max-w-sm">
                                    By sending you agree to be contacted by BPO
                                    Solutions about your enquiry. We never share
                                    your data.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="contact-submit-button"
                                    className="inline-flex items-center justify-center gap-2 bg-bpo-ink hover:bg-bpo-charcoal text-white font-semibold py-3.5 px-7 rounded-full transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Send brief
                                            <ArrowUpRight className="h-4 w-4" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
